// FILE: UserProfilePage.js

import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    MenuItem,
    Button,
    Chip,
    CircularProgress,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stack,
    Divider
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
//import axios from "axios";
import api from '../components/axios/axiosInstance'
// Blacklisted domains (add/remove as needed)
const EMAIL_BLACKLIST = [
    "@gmail.com",
    //"@outlook.com",
    //"@outlook.ca",
    //"@hotmail.com",
    "@yahoo.com",
    "@icloud.com",
    "@aol.com",
    "@mail.com",
    "@protonmail.com"
];

// Mock school list (replace with real schools from API)
const mockSchools = [
    { id: 1, name: "Green Valley Elementary" },
    { id: 2, name: "Blue Ridge Academy" },
    { id: 3, name: "Sunrise School" }
];

const VERIFICATION_LIMIT = 20;
const VERIFICATION_GAP = 0//3 * 60 * 1000; // 3 min in ms
const LOCKOUT_2H = 2 * 60 * 60 * 1000; // 2 hours in ms
const LOCKOUT_24H = 24 * 60 * 60 * 1000; // 24 hours in ms

function getEmailDomain(email) {
    if (!email) return "";
    const atIdx = email.indexOf("@");
    return atIdx > -1 ? email.substring(atIdx) : "";
}

const getNow = () => new Date().getTime();

const UserProfilePage = () => {
    // Profile state
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);

    // Form fields
    const [displayName, setDisplayName] = useState("");
    const [schoolId, setSchoolId] = useState("");
    const [schoolEmail, setSchoolEmail] = useState("");

    // Snackbar state
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [updating, setUpdating] = useState(false);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [verifying, setVerifying] = useState(false);

    // Rate limit state
    const [sendCount, setSendCount] = useState(0);
    const [lastSent, setLastSent] = useState(null);
    const [lockoutUntil, setLockoutUntil] = useState(null);
    const [lockoutType, setLockoutType] = useState(""); // '2h' or '24h'
    const timerRef = useRef(null);

    //Deleting Profile
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);


    // Restore limits from localStorage
    useEffect(() => {
        const saved = JSON.parse(localStorage.getItem("sg_school_email_verify_limit") || "{}");
        setSendCount(saved.sendCount || 0);
        setLastSent(saved.lastSent || null);
        setLockoutUntil(saved.lockoutUntil || null);
        setLockoutType(saved.lockoutType || "");
    }, []);

    // Save limits to localStorage
    useEffect(() => {
        localStorage.setItem(
            "sg_school_email_verify_limit",
            JSON.stringify({ sendCount, lastSent, lockoutUntil, lockoutType })
        );
    }, [sendCount, lastSent, lockoutUntil, lockoutType]);

    // Fetch user profile on mount
    useEffect(() => {
        const fetchProfile = async () => {
            try {

                const res = await api.get(`${process.env.REACT_APP_BACKEND_URL}/auth/profile/`, { withCredentials: true });
                const data = res.data;
                setProfile(data);
                setDisplayName(data.display_name || "");
                setSchoolId(data.school?.id || "");
                setSchoolEmail(data.school_email || "");
            } catch (err) {
                setSnackbar({ open: true, message: "Failed to load profile.", severity: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    // Helper: reset all verification state (after 24h lockout)
    const resetVerificationState = () => {
        setSendCount(0);
        setLastSent(null);
        setLockoutUntil(null);
        setLockoutType("");
        localStorage.removeItem("sg_school_email_verify_limit");
    };

    // Check email before opening modal
    const handleOpenModal = () => {
        if (!schoolEmail || schoolEmail.trim() === "") {
            setSnackbar({ open: true, message: "Please enter your school email first.", severity: "warning" });
            return;
        }
        const domain = getEmailDomain(schoolEmail.toLowerCase());
        if (EMAIL_BLACKLIST.includes(domain)) {
            setSnackbar({
                open: true,
                message: "Please enter your official school/work email, not a public provider.",
                severity: "error"
            });
            return;
        }
        if (profile.school_email_verified) {
            setSnackbar({
                open: true,
                message: "Your school email is already verified.",
                severity: "info"
            });
            return;
        }
        setModalOpen(true);
    };

    // Close modal and clear code field
    const handleCloseModal = () => {
        setModalOpen(false);
        setVerificationCode("");
    };

    // Handle Send/Resend Code button logic
    const handleSendCode = async () => {
        const now = getNow();

        // If locked out
        if (lockoutUntil && now < lockoutUntil) {
            let waitMsg = lockoutType === "24h"
                ? "You can request a new code after 24 hours."
                : "You can request a new code after 2 hours.";
            setSnackbar({ open: true, message: waitMsg, severity: "error" });
            return;
        }

        // If already hit limit, apply lockout
        if (sendCount >= VERIFICATION_LIMIT) {
            // If already did 2h lockout, escalate to 24h
            if (lockoutType === "2h") {
                setLockoutUntil(now + LOCKOUT_24H);
                setLockoutType("24h");
                setSnackbar({
                    open: true,
                    message: "You have requested too many codes. Please try again after 24 hours.",
                    severity: "error"
                });
            } else {
                setLockoutUntil(now + LOCKOUT_2H);
                setLockoutType("2h");
                setSnackbar({
                    open: true,
                    message: "You have requested too many codes. Please try again after 2 hours.",
                    severity: "error"
                });
            }
            return;
        }

        // 3 minute gap enforcement
        if (lastSent && now - lastSent < VERIFICATION_GAP) {
            const mins = Math.ceil((VERIFICATION_GAP - (now - lastSent)) / 60000);
            setSnackbar({
                open: true,
                message: `Please wait ${mins} minute(s) before requesting a new code.`,
                severity: "info"
            });
            return;
        }

        try {

            await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/send-verification-code/`,
                { school_email: schoolEmail },
                { withCredentials: true }
            );
            setSnackbar({ open: true, message: "Verification code sent to your school email.", severity: "success" });
            setSendCount(sendCount + 1);
            setLastSent(now);

            // Start countdown for next request (for UI, optional)
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => { }, VERIFICATION_GAP);
        } catch (err) {
            setSnackbar({ open: true, message: "Failed to send verification code.", severity: "error" });
        }
    };

    // Handle verify code
    const handleVerifyCode = async () => {
        setVerifying(true);
        try {

            const res = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/auth/verify-code/`,
                { code: verificationCode }

            );
            setSnackbar({ open: true, message: res.data?.message || "Email verified!", severity: "success" });

            // Refetch profile to update verification badge
            const refreshed = await api.get(`${process.env.REACT_APP_BACKEND_URL}/auth/profile/`);
            setProfile(refreshed.data);
            handleCloseModal();
            resetVerificationState();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.error || "Verification failed.",
                severity: "error"
            });
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>;
    }
    if (!profile) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="error" sx={{ mb: 2 }}>Failed to load profile.</Typography>
                <Button variant="outlined" onClick={() => window.location.reload()}>Retry</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    <AccountCircleIcon sx={{ mr: 1 }} />
                    User Profile
                </Typography>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={profile.first_name}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={profile.last_name}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Display Name"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                        <Typography variant="caption" color="text.secondary">
                            This name will be displayed on your profile.
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email (Login)"
                            value={profile.email}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="School"
                            value={schoolId}
                            onChange={(e) => setSchoolId(e.target.value)}
                        >
                            <MenuItem value="">Select School</MenuItem>
                            {mockSchools.map((school) => (
                                <MenuItem key={school.id} value={school.id}>
                                    {school.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            label="School Email"
                            value={schoolEmail}
                            onChange={(e) => setSchoolEmail(e.target.value)}
                            InputProps={{
                                style: { fontWeight: 600, letterSpacing: 1 }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}>
                        {profile.school_email_verified ? (
                            <Chip
                                label="Verified"
                                color="success"
                                icon={<VerifiedIcon />}
                                sx={{ fontWeight: 600, mr: 2 }}
                            />
                        ) : (
                            <Chip
                                label="Unverified"
                                color="warning"
                                icon={<ErrorOutlineIcon />}
                                sx={{ fontWeight: 600, mr: 2 }}
                            />
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleOpenModal}
                            disabled={profile.school_email_verified}
                            sx={{ ml: 2 }}
                        >
                            Verify School Email
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                setUpdating(true);
                                try {

                                    await api.patch(
                                        `${process.env.REACT_APP_BACKEND_URL}/auth/profile/update/`,
                                        {
                                            display_name: displayName,
                                            school_id: schoolId || null,
                                            school_email: schoolEmail
                                        },
                                        { withCredentials: true }
                                    );
                                    setSnackbar({ open: true, message: "Profile updated!", severity: "success" });
                                    // Refetch to get updated verification status etc.
                                    const res = await api.get(`${process.env.REACT_APP_BACKEND_URL}/auth/profile/`, { withCredentials: true });
                                    setProfile(res.data);
                                } catch (err) {
                                    setSnackbar({ open: true, message: "Failed to update profile.", severity: "error" });
                                } finally {
                                    setUpdating(false);
                                }
                            }}
                            disabled={updating}
                        >
                            {updating ? "Saving..." : "Save Profile"}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            {/* Verification Modal */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, p: 2 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
                    Verify School Email
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseModal}
                        sx={{
                            position: "absolute",
                            right: 10,
                            top: 10,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        To verify <b>{schoolEmail}</b>, please request a code and enter it below.
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            onClick={handleSendCode}
                            disabled={sendCount >= VERIFICATION_LIMIT || !!lockoutUntil}
                        >
                            Send Code
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleSendCode}
                            disabled={sendCount >= VERIFICATION_LIMIT || !!lockoutUntil}
                        >
                            Resend Code
                        </Button>
                    </Stack>
                    <TextField
                        fullWidth
                        label="Enter Verification Code"
                        value={verificationCode}
                        onChange={e => setVerificationCode(e.target.value)}
                        inputProps={{ maxLength: 10, style: { letterSpacing: 2 } }}
                        sx={{ mb: 2, mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
                    <Button onClick={handleCloseModal} color="secondary" variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleVerifyCode}
                        disabled={verifying || !verificationCode}
                    >
                        {verifying ? "Verifying..." : "Verify"}
                    </Button>
                </DialogActions>
                <Box sx={{ textAlign: "center", pb: 2 }}>
                    {lockoutUntil && (
                        <Typography variant="caption" color="error">
                            {lockoutType === "24h"
                                ? "Too many attempts. Try again after 24 hours."
                                : "Too many attempts. Try again after 2 hours."}
                        </Typography>
                    )}
                    {!lockoutUntil && sendCount > 0 && sendCount < VERIFICATION_LIMIT && lastSent && (
                        <Typography variant="caption" color="text.secondary">
                            {`You can request ${VERIFICATION_LIMIT - sendCount
                                } more code(s).`}
                        </Typography>
                    )}
                </Box>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, textAlign: "center", color: "error.main" }}>
                    ⚠️ Confirm Account Deletion
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>This action is irreversible.</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Deleting your account will permanently remove all your data and all students you created.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        onClick={async () => {
                            setDeleting(true);
                            try {

                                await api.delete(`${process.env.REACT_APP_BACKEND_URL}/auth/delete-account/`, {
                                    withCredentials: true
                                });


                                window.location.href = "/";
                            } catch (err) {
                                setSnackbar({
                                    open: true,
                                    message: "❌ Failed to delete profile. Please try again.",
                                    severity: "error"
                                });
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ mt: 4, textAlign: "center" }}>
                <Divider sx={{ mb: 3 }} />
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ fontWeight: "bold", px: 4 }}
                >
                    🗑️ Delete My Profile
                </Button>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserProfilePage;
